// Public invite page (SPEC §19, 2026-10-01): /invite?token=<the emailed token>. Shown before sign-in, styled like
// Login.tsx. Checks the link (GET /api/auth/invite/:token), then the teammate sets a password and lands in the app
// signed in (POST /api/auth/invite/:token/accept -> Me + the session cookie).
import '../dialer/dialer.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Me } from '../../../src/shared/api';
import type { InvitePublic } from '../../../src/shared/api-team';
import { api, ApiFailure, errorMessage } from '../api';
import { ErrorBanner } from '../components/ui';
import { Field } from '../components/ui-extra';

const MIN_PASSWORD = 10;   // the server's rule (lib/crypto.ts passwordProblem)

function explain(e: unknown): string {
  if (e instanceof ApiFailure) return e.body.message || `Something went wrong (${e.status}).`;
  if (e instanceof TypeError) return "Can't reach the dialer server. Check your internet connection and try again.";
  return errorMessage(e);
}

const longDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

export function InviteAccept(props: { onSignedIn: (me: Me) => void }) {
  const token = new URLSearchParams(location.search).get('token')?.trim() ?? '';
  const [invite, setInvite] = useState<InvitePublic | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pwRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) { setLoadError('This link is missing its invite code. Open the link from your invite email again.'); return; }
    api.get<InvitePublic>(`/api/auth/invite/${encodeURIComponent(token)}`)
      .then((inv) => { setInvite(inv); setTimeout(() => pwRef.current?.focus(), 0); })
      .catch((e) => setLoadError(explain(e)));
  }, [token]);

  async function submit(e: Event) {
    e.preventDefault();
    if (busy || !invite) return;
    setError(null);
    if (password.length < MIN_PASSWORD) { setError(`Use at least ${MIN_PASSWORD} characters. A short sentence you'll remember works well.`); return; }
    if (password !== confirm) { setError("The two passwords don't match."); return; }
    setBusy(true);
    try {
      const me = await api.post<Me>(`/api/auth/invite/${encodeURIComponent(token)}/accept`, { password, confirm });
      props.onSignedIn(me);
    } catch (err) {
      setError(explain(err));
      setPassword('');
      setConfirm('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="vd-login">
      <div class="card">
        <div class="vd-login-brand"><div>Valistix<span>.</span> Dialer</div></div>
        {!invite && !loadError ? <div class="muted">Checking your invite…</div> : null}

        {loadError ? (
          <div class="stack" style="margin-top:12px">
            <ErrorBanner error={loadError} />
            <a class="btn" href="/" style="text-align:center;text-decoration:none">Go to sign in</a>
            <p class="muted" style="margin:0;font-size:13px">Questions? Email <a href="mailto:pj@valistix.com">pj@valistix.com</a>.</p>
          </div>
        ) : null}

        {invite ? (
          <>
            <h2 style="margin:12px 0 4px">Welcome, {invite.firstName}</h2>
            <div class="muted">
              You've been invited to the Valistix Dialer as {invite.role === 'admin' ? 'an admin' : 'a setter'}. Choose a password to create your account.
            </div>
            <form onSubmit={submit} autocomplete="on">
              <Field label="Email">
                <input type="email" value={invite.email} readOnly autocomplete="username" style="background:var(--sand)" />
              </Field>
              <Field label="Password" hint={`At least ${MIN_PASSWORD} characters.`}>
                <input ref={pwRef} type={shown ? 'text' : 'password'} value={password} onInput={(e) => setPassword(e.currentTarget.value)} autocomplete="new-password" />
              </Field>
              <Field label="Password again">
                <input type={shown ? 'text' : 'password'} value={confirm} onInput={(e) => setConfirm(e.currentTarget.value)} autocomplete="new-password" />
              </Field>
              <label class="row" style="font-size:13px;gap:6px">
                <input type="checkbox" checked={shown} onChange={(e) => setShown(e.currentTarget.checked)} /> Show passwords
              </label>
              <ErrorBanner error={error} />
              <button type="submit" class="btn btn-primary" disabled={busy}>{busy ? 'Creating your account…' : 'Create my account'}</button>
            </form>
            <p class="muted" style="margin:14px 0 0;font-size:13px">
              This invite works once and expires {longDate(invite.expiresAt)}. Keep your password to yourself; nobody at Valistix will ask for it.
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
